/* ============================================================================
   Modul-Detail-Texte (DE) — werden im ModuleDetailDrawer auf Seite 1 verwendet.
   Inhalte sind kontrolliert und entsprechen der cloudtec-Stilrichtlinie.
   EN-Übersetzungen folgen als separater Schritt — Fallback auf DE.
   ============================================================================ */

const MODULE_DETAILS_DE = {
  // ---------- Basis ----------
  user: {
    sum: 'Benutzer, Rollen, Self-Service',
    long: 'Verwalten Sie Benutzer, Rollen und Organisationen zentral auf einer Plattform. Self-Service-Funktionen für Registrierung, Passwort-Reset und Profilpflege entlasten Administration und Support.',
    items: [
      'Benutzer- und Rollenverwaltung',
      'Organisationen und Gruppen',
      'Self-Service-Registrierung',
      'Passwort-Reset und Benutzeraktivierung',
      'Benutzerprofile und Einstellungen',
      'Delegationen und Stellvertretungen',
      'Rollenbasierte Plattformzugriffe',
      'Integration mit SSO und MFA',
    ],
    benefit: 'Neue Mitarbeitende richten ihren Zugang selbstständig ein und erhalten automatisch die passenden Rollen. Jeder Zugriff bleibt nachvollziehbar in einer auditierbaren Benutzerübersicht.',
    example: 'Mitarbeitende eines Unternehmens registrieren sich über die Self-Service-Funktion. Die Plattform weist Rollen anhand der E-Mail-Domain und der Organisationszugehörigkeit automatisch zu.',
  },
  perm: {
    sum: 'Rollen, Rechte, Vererbung',
    long: 'Steuern Sie Zugriffe feingranular über Rollen, Objekte und Status. Rechtevererbung, Mandantenkontext und Genehmigungslogik decken auch komplexe Organisationsstrukturen ab.',
    items: [
      'Rollenbasierte Berechtigungen',
      'Objekt- und datensatzbasierte Zugriffe',
      'Dynamische Rechte abhängig von Status oder Kontext',
      'Sichtbarkeitssteuerung',
      'Freigabe- und Genehmigungsrechte',
      'Mandantenabhängige Rechte',
      'Rechtevererbung',
      'Auditierbare Berechtigungsänderungen',
    ],
    benefit: 'Sie bilden externe Partner, interne Teams und mehrere Mandanten in einem Berechtigungsmodell ab. Jede Änderung an Rechten ist mit Benutzer, Zeitstempel und Vorzustand protokolliert.',
    example: 'Ein externer Partner sieht ausschliesslich die eigenen Projekte. Interne Mitarbeitende erhalten zusätzlich die Administrationsfunktionen, die ihrer Rolle entsprechen.',
  },
  auth: {
    sum: 'Login, MFA, Passkeys',
    long: 'Schützen Sie Zugriffe mit modernen Authentifizierungsverfahren. MFA, Passkeys, Session-Management und Passwort-Richtlinien sind durchgängig auditierbar.',
    items: [
      'Benutzername/Passwort-Login',
      'Multi-Faktor-Authentifizierung (MFA)',
      'Passkey-Unterstützung',
      'Session-Management',
      'Passwort-Richtlinien',
      'Geräte- und Zugriffsvalidierung',
      'Brute-Force-Schutz',
      'Authentifizierungs-Logging',
    ],
    benefit: 'Sie schützen interne und externe Zugriffe mit kombinierbaren Verfahren. Authentifizierungsereignisse fliessen in das zentrale Logging und stehen für Sicherheits-Reviews bereit.',
    example: 'Mitarbeitende melden sich mit Passkey an. Externe Partner nutzen MFA über eine Authenticator-App — jeder Anmeldeversuch ist protokolliert.',
  },
  data: {
    sum: 'Strukturiert, validiert, versioniert',
    long: 'Modellieren Sie Geschäftsdaten mit klaren Beziehungen, Status und Lifecycle-Regeln. Validierungen greifen serverseitig und halten Datenqualität auch bei vielen parallelen Bearbeitern stabil.',
    items: [
      'Strukturierte Datenmodelle',
      'Validierungen und Datenregeln',
      'Historisierung und Versionierung',
      'Beziehungen zwischen Datensätzen',
      'Status- und Lifecycle-Management',
      'Datenintegrationen',
      'Workflow-Abhängigkeiten',
      'Import- und Synchronisationsprozesse',
    ],
    benefit: 'Sie halten Stammdaten, Bewegungsdaten und Audit-Informationen sauber getrennt. Versionierung macht Änderungen über die gesamte Datenhistorie hinweg nachvollziehbar.',
    example: 'Patientendaten, Zuweisungen und Statusänderungen werden versioniert gespeichert. Jeder frühere Stand bleibt mit Benutzer und Zeitstempel rekonstruierbar.',
  },
  lists: {
    sum: 'Sortieren, filtern, exportieren',
    long: 'Verarbeiten Sie grosse Datenmengen in dynamischen Tabellen mit Filtern, Gruppierung und gespeicherten Ansichten. Massenaktionen und Inline-Bearbeitung beschleunigen die tägliche Arbeit.',
    items: [
      'Dynamische Tabellenansichten',
      'Filter und Suchfunktionen',
      'Sortierung und Gruppierung',
      'Gespeicherte Ansichten',
      'Massenaktionen',
      'Rollenabhängige Ansichten',
      'Inline-Bearbeitung',
      'Exportfunktionen',
    ],
    benefit: 'Sie analysieren tausende Datensätze ohne Performance-Einbruch. Gespeicherte Ansichten und rollenbasierte Filter passen sich an jeden Anwendungsfall an.',
    example: 'Ein Sachbearbeiter filtert alle offenen Fälle mit hoher Priorität. Die gefilterte Ansicht wird gespeichert und als wiederkehrender Excel-Export angelegt.',
  },
  api: {
    sum: 'REST, versioniert, dokumentiert',
    long: 'Stellen Sie strukturierte Schnittstellen für interne Anwendungen und Drittsysteme bereit. REST- und GraphQL-Endpunkte sind versioniert, token-authentifiziert und rate-limited.',
    items: [
      'REST- und GraphQL-APIs',
      'API-Versionierung',
      'Authentifizierung und Tokens',
      'Rate-Limiting',
      'API-Dokumentation',
      'Monitoring und Logging',
      'Webhooks und Events',
      'Rollenbasierte API-Zugriffe',
    ],
    benefit: 'Sie binden ERP, CRM und Fachsysteme über dokumentierte Endpunkte an. Webhooks und API-Logs machen jede Datenbewegung nachvollziehbar.',
    example: 'Ein ERP-System synchronisiert Kundendaten und Rechnungsinformationen mit der Plattform. Statusänderungen werden über signierte Webhooks an das ERP zurückgemeldet.',
  },
  search: {
    sum: 'Volltext, semantisch, facettiert',
    long: 'Durchsuchen Sie Inhalte, Daten und Dokumente über alle Domänen hinweg. Synonyme, Gewichtungen und Autocomplete liefern relevante Treffer auch bei unscharfen Begriffen.',
    items: [
      'Volltextsuche',
      'Facetten und Filter',
      'Synonyme und Gewichtungen',
      'Mehrdomänen-Suche',
      'Dokumentensuche',
      'Semantische Suchfunktionen',
      'Rollenabhängige Suchresultate',
      'Suchvorschläge und Autocomplete',
    ],
    benefit: 'Sie finden Projekte, Dokumente und Tickets in einer Suchabfrage. Rollenabhängige Resultate respektieren bestehende Berechtigungen automatisch.',
    example: 'Ein Benutzer sucht nach einer Kundennummer. Die Trefferliste zeigt offene Tickets, laufende Projekte und zugehörige Dokumente in einer einzigen Ansicht.',
  },
  cache: {
    sum: 'Application, API, Edge',
    long: 'Beschleunigen Sie Plattform-Antworten über mehrstufiges Caching. Application-, API- und Edge-Cache greifen koordiniert und entlasten die Datenbank gezielt.',
    items: [
      'Server- und Application-Caching',
      'Datenbank-Caching',
      'API-Caching',
      'CDN-Integration',
      'Cache-Invalidierung',
      'Session-Caching',
      'Edge-Caching',
      'Skalierungsoptimierungen',
    ],
    benefit: 'Sie halten Antwortzeiten auch bei hoher Last und grossen Datenmengen stabil. Cache-Invalidierung über Events hält die Daten gleichzeitig aktuell.',
    example: 'Häufig aufgerufene Listen werden zwischengespeichert und ohne Datenbankzugriff ausgeliefert. Änderungen invalidieren den Cache automatisch.',
  },
  'admin-ui': {
    sum: 'Konfiguration im Browser',
    long: 'Administrieren Sie Benutzer, Rollen, Workflows und Systemeinstellungen über eine zentrale Oberfläche. Dashboards und Audit-Übersichten sind direkt eingebunden.',
    items: [
      'Benutzer- und Rollenverwaltung',
      'Konfigurationsoberflächen',
      'Dashboards und Reports',
      'Workflow-Steuerung',
      'Datenverwaltung',
      'Rechteverwaltung',
      'Systemkonfigurationen',
      'Audit- und Aktivitätsübersichten',
    ],
    benefit: 'Sie konfigurieren Plattformmodule ohne Code-Änderungen. Fachverantwortliche steuern Prozesse selbst — die Entwicklung bleibt für komplexere Anpassungen frei.',
    example: 'Administratoren legen einen neuen Workflow inklusive Freigabeschritten direkt im Browser an. Änderungen sind ohne Deployment sofort wirksam.',
  },
  media: {
    sum: 'Upload, Varianten, CDN',
    long: 'Verwalten Sie Bilder, Videos und Dokumente zentral in einer Medienbibliothek. Variantenbildung, Bildoptimierung und CDN-Auslieferung erfolgen automatisch.',
    items: [
      'Datei-Uploads',
      'Medienbibliothek',
      'Variantenbildung',
      'Bildoptimierung',
      'Metadatenverwaltung',
      'Zugriffskontrolle',
      'CDN-Auslieferung',
      'Versionierung',
    ],
    benefit: 'Sie liefern Medien performant über das CDN aus. Zugriffskontrolle und Versionierung greifen auch für sensible oder kundenspezifische Inhalte.',
    example: 'Ein hochgeladenes Produktbild wird automatisch in mehreren Grössen erzeugt und optimiert ausgeliefert. Der Originalbild-Zugriff ist auf interne Rollen beschränkt.',
  },
  export: {
    sum: 'CSV, Excel, JSON',
    long: 'Exportieren Sie Daten ad hoc oder nach Zeitplan in gängige Formate. Filter, Rollenrechte und Export-Historie sind durchgängig integriert.',
    items: [
      'CSV- und Excel-Export',
      'JSON- und XML-Export',
      'Geplante Exporte',
      'Rollenbasierte Exportrechte',
      'Filterabhängige Datenexports',
      'PDF-Generierung',
      'API-basierte Exporte',
      'Exporthistorisierung',
    ],
    benefit: 'Sie übergeben Auswertungen an Excel, BI-Tools oder externe Systeme. Geplante Exporte laufen ohne manuellen Aufwand zuverlässig im Hintergrund.',
    example: 'Ein monatlicher Projektreport wird automatisiert als Excel-Datei erzeugt und an das Controlling versendet. Die Export-Historie dokumentiert jeden Lauf.',
  },
  notify: {
    sum: 'E-Mail, Push, In-App',
    long: 'Steuern Sie Benachrichtigungen über E-Mail, Push, SMS und In-App-Kanäle zentral. Vorlagen, Mehrsprachigkeit und Eskalationen sind integriert.',
    items: [
      'E-Mail-Benachrichtigungen',
      'Push-Mitteilungen',
      'In-App-Notifications',
      'SMS-Versand',
      'Vorlagenverwaltung',
      'Mehrsprachige Benachrichtigungen',
      'Eskalationen',
      'Zustellstatus und Logs',
    ],
    benefit: 'Sie informieren Benutzer zeitnah über Statusänderungen und Freigabeschritte. Zustellstatus und Logs machen jede Benachrichtigung nachvollziehbar.',
    example: 'Bei Statusänderung eines Vorgangs erhalten zuständige Mitarbeitende eine In-App-Benachrichtigung. Bleibt die Reaktion aus, eskaliert das System nach definierter Frist per E-Mail.',
  },

  // ---------- Hilfs ----------
  i18n: {
    sum: 'Inhalte mehrsprachig pflegen',
    long: 'Pflegen Sie Inhalte, Oberflächen und Systemtexte in mehreren Sprachen. Übersetzungs-Workflows, Fallback-Sprachen und sprachspezifische Benachrichtigungen sind integriert.',
    items: [
      'Mehrsprachige Inhalte',
      'Sprachabhängige Benutzeroberflächen',
      'Übersetzungs-Workflows',
      'Fallback-Sprachen',
      'Mehrsprachige E-Mails',
      'Sprachspezifische Inhalte',
      'Sprachumschaltung',
      'Übersetzungsverwaltung',
    ],
    benefit: 'Sie betreiben internationale Plattformen mit konsistenter Sprachführung. Benutzer wechseln die Sprache jederzeit ohne Datenverlust oder Kontextbruch.',
    example: 'Eine Plattform wird parallel in Deutsch, Französisch und Italienisch betrieben. Benachrichtigungen werden automatisch in der Sprache des jeweiligen Empfängers versendet.',
  },
  reporting: {
    sum: 'KPIs, Trends, Drilldowns',
    long: 'Analysieren Sie Plattformdaten in Echtzeit über konfigurierbare Dashboards. Diagramme, Drilldowns und zeitbasierte Reports decken operative und strategische Fragen ab.',
    items: [
      'KPI-Dashboards',
      'Diagramme und Statistiken',
      'Echtzeit-Auswertungen',
      'Exportfunktionen',
      'Filter und Drilldowns',
      'Zeitbasierte Reports',
      'Rollenabhängige Dashboards',
      'Individuelle Reportdefinitionen',
    ],
    benefit: 'Sie sehen Projektstatus, Ticketvolumen und Umsatzentwicklung in einer Übersicht. Rollenabhängige Dashboards liefern jeder Funktion die für sie relevanten Kennzahlen.',
    example: 'Die Geschäftsleitung analysiert wöchentlich Umsatz- und Auftragsentwicklung über ein zentrales Dashboard. Drilldowns führen direkt zu einzelnen Vorgängen.',
  },
  pdf: {
    sum: 'Rechnungen, Verträge, Reports',
    long: 'Generieren Sie Rechnungen, Verträge und Reports serverseitig aus Plattformdaten. Vorlagen, Mehrsprachigkeit und digitale Signaturen sind integriert.',
    items: [
      'PDF-Generierung',
      'Vorlagenverwaltung',
      'Mehrsprachige Dokumente',
      'Rechnungen und Verträge',
      'Automatisierter Versand',
      'Digitale Signaturen',
      'Versionierung',
      'Layout-Anpassungen',
    ],
    benefit: 'Sie erstellen Dokumente automatisiert und konsistent aus laufenden Geschäftsprozessen. Versionierung und Layout-Anpassungen bleiben jederzeit kontrollierbar.',
    example: 'Eine Bestellung wird abgeschlossen, das System generiert die Rechnung als PDF und versendet sie automatisch. Eine versionierte Kopie bleibt im Kundenakt.',
  },
  logging: {
    sum: 'Strukturierte Audit-Trails',
    long: 'Protokollieren Sie technische und fachliche Aktivitäten revisionssicher. Benutzeraktionen, API-Aufrufe und Systemereignisse sind durchgehend nachvollziehbar.',
    items: [
      'Audit-Trails',
      'Benutzeraktivitäten',
      'API-Logging',
      'Fehlerprotokollierung',
      'Statusänderungen',
      'Systemereignisse',
      'Sicherheitslogs',
      'Exportierbare Protokolle',
    ],
    benefit: 'Sie weisen Änderungen an sensiblen Datensätzen lückenlos nach. Exportierbare Protokolle unterstützen Compliance-Reviews und interne Audits.',
    example: 'Jede Änderung an einem Patientendatensatz wird mit Benutzer, Zeitstempel und Vorzustand protokolliert. Das Audit-Log lässt sich für eine Revisionsprüfung exportieren.',
  },

  // ---------- KI ----------
  'ai-gateway': {
    sum: 'Zentrale Schicht für externe & lokale Modelle',
    long: 'Zentrale KI-Integrationsschicht zur Anbindung und Verwaltung verschiedener Large Language Models (LLMs). Externe KI-Dienste und lokal betriebene Modelle werden einheitlich angesprochen.',
    items: [
      'Einheitliche API für GPT, Claude, Mistral, lokale LLMs',
      'Modell-Routing nach Kosten, Performance, Datenklassifikation',
      'Zugriffskontrolle und Mandantenfähigkeit',
      'Kosten- und Nutzungsmonitoring pro Team/Mandant',
      'API-Management, Rate-Limits, Caching',
      'Sicherheits- und Compliance-Layer (PII-Maskierung optional)',
    ],
    benefit: 'KI-Funktionen kontrollierbar, nachvollziehbar und unabhängig von einzelnen Anbietern in Plattform und Geschäftsprozesse integrieren — ohne Lock-in.',
    example: 'Marketing-Team nutzt OpenAI, Legal nutzt lokales Modell — beide über dieselbe Plattform-API. Wechsel der Modelle ohne Code-Änderungen in den Anwendungen.',
  },
  'ai-pipelines': {
    sum: 'Pipelines, RAG, Dokumenten-KI on-prem',
    long: 'Individuelle KI-Verarbeitungspipelines für spezifische Geschäftsprozesse und Datenflüsse. Lokal gehostete Modelle innerhalb der eigenen Infrastruktur — sensible Daten verlassen die kontrollierte Umgebung nicht.',
    items: [
      'Lokales Hosting von Open-Source-LLMs',
      'Dokumentenanalyse, Klassifikation, Extraktion',
      'Semantische Suche & Retrieval-Augmented Generation (RAG)',
      'Anbindung an Dokumente, Datenbanken, APIs, bestehende Plattformen',
      'Vektordatenbanken & Embedding-Pipelines',
      'Versionierte Prompts und reproduzierbare Workflows',
    ],
    benefit: 'Individuelle, datenschutzkonforme KI-Workflows mit hoher Flexibilität und Nachvollziehbarkeit. Unternehmenswissen wird strukturiert für KI verfügbar gemacht.',
    example: 'Vertragsanalyse: hochgeladene PDFs werden lokal klassifiziert, Klauseln extrahiert, Risiken markiert — kein Vertrag verlässt das CH-Hosting.',
  },
  'ai-agents': {
    sum: 'Agenten orchestrieren ERP/CRM/Fachsysteme',
    long: 'Intelligente KI-Agenten zur Automatisierung komplexer Geschäftsprozesse. Agenten analysieren Aufgaben, bereiten Entscheidungen vor, binden externe Systeme an und orchestrieren mehrstufige Abläufe.',
    items: [
      'Standardisierte Tool-Integration (MCP, OpenAPI)',
      'Anbindung an ERP, CRM, Fachsysteme',
      'Workflow-Engine: klassische Prozesse + KI-Entscheidungen',
      'Freigaben, Eskalationen, Rollenrechte',
      'Human-in-the-Loop bei kritischen Schritten',
      'Vollständige Nachvollziehbarkeit jeder Aktion',
    ],
    benefit: 'Komplexe Geschäftsprozesse werden zu kontrollierbaren, nachvollziehbaren KI-Workflows — mit klaren Übergabepunkten an Menschen.',
    example: 'Lieferanten-Onboarding: Agent prüft Unterlagen, fragt SAP/CRM ab, schlägt Risiko-Klassifikation vor — Compliance-Officer bestätigt.',
  },
  'ai-ux': {
    sum: 'Natürliche Sprache, Auto-Fill, semantische Suche',
    long: 'Klassische Benutzeroberflächen werden um intelligente KI-Funktionen erweitert. Anwender suchen, analysieren und lösen Prozesse über natürliche Sprache aus.',
    items: [
      'AI Search & semantische Suche im UI',
      'Intelligente Formularunterstützung & Auto-Fill',
      'Automatische Datenextraktion aus Dokumenten',
      'Vorschläge & Erklärungen kontextbezogen',
      'Multi-Modal: Text, Bild, Sprache',
      'Lernfähig über Benutzer-Feedback',
    ],
    benefit: 'Moderne, benutzerfreundliche Plattformen mit reduziertem Schulungsaufwand. Anwender erreichen schneller das gewünschte Ergebnis.',
    example: 'Anwender tippt „Alle offenen Rechnungen aus Q3 von Kunden im Tessin" — System filtert, gruppiert, schlägt Exportformat vor.',
  },

  // ---------- Stack CMS by cloudtec ----------
  'stack-cms': {
    sum: 'Page Builder, Editor, Headless API',
    long: 'Pflegen Sie Websites über einen Block-basierten Page Builder mit Live-Vorschau und Headless-API-Auslieferung. Mehrsprachige Workflows pro Mandant und granulare Rollensteuerung gehören zur Basis.',
    items: [
      'Page Builder mit Block-System',
      'Richtext-Editor (WYSIWYG)',
      'Mehrsprachigkeit und Übersetzungs-Workflows',
      'Medien-Verwaltung mit Variantenbildung',
      'Live-Vorschau für Desktop, Tablet und Mobile',
      'Rollen und Berechtigungen pro Mandant',
      'Headless API (REST)',
      'Auslieferung an Next.js, Nuxt, native Apps oder statische Sites',
    ],
    benefit: 'Sie pflegen beliebig viele Kunden-Websites mit mehreren Sprachversionen auf einer Instanz. Was in der Vorschau sichtbar ist, entspricht exakt dem, was am Frontend publiziert wird.',
    example: 'Eine Agentur betreibt 30 Kunden-Websites auf einer Instanz. Jeder Kunde pflegt Inhalte in Deutsch, Französisch und Englisch — mit eigenen Rollen, eigenem Layout und Auslieferung an unterschiedliche Frontend-Technologien.',
  },
  'stack-ai-writing': {
    sum: 'Schreiben, übersetzen, optimieren',
    long: 'Beschleunigen Sie Texterstellung und Übersetzung direkt im Editor. Writing Assistant, Text-zu-Seite und KI-Übersetzung arbeiten mit dem Tonalitätsprofil und Auto-Glossar Ihres Mandanten.',
    items: [
      'Writing Assistant (Generieren, Verbessern, Übersetzen im Editor)',
      'Text-zu-Seite (komplette Seiten aus Prompt oder Vorlage)',
      'Tonalitätsprofil und Auto-Glossar pro Mandant',
      'KI-Vorab-Übersetzungen auf Knopfdruck',
      'SEO-Assistent (Meta-Titel, Beschreibung, Heading-Hierarchie, Keywords)',
      'Interne Link-Vorschläge auf semantischer Basis',
      'KI-Verschlagwortung und Alt-Text-Generierung für Medien',
    ],
    benefit: 'Sie erstellen mehrsprachige Inhalte in Minuten statt Stunden — mit konsistenter Tonalität, optimierter SEO und barrierefreien Alt-Texten. Schreibstil und Fachwortschatz jedes Mandanten werden konsistent angewendet.',
    example: 'Eine Redakteurin verfasst einen Artikel auf Deutsch. Die KI übersetzt ihn in zwei weitere Sprachen, schlägt interne Verlinkungen vor und ergänzt Meta-Daten — alles im selben Bearbeitungsfluss.',
  },
  'stack-dashboard': {
    sum: 'Vollständigkeit, Links, Kohärenz',
    long: 'Behalten Sie pro Mandant den Überblick über Qualität und Konsistenz aller Inhalte. Vollständigkeits-Score, Broken-Link-Erkennung, SEO-Status und Übersetzungs-Kohärenz liegen in einer aggregierten Sicht.',
    items: [
      'Vollständigkeits-Score pro Seite',
      'Automatische Broken-Link-Erkennung (intern und extern)',
      'Aggregierter SEO-Status über alle Seiten',
      'Übersetzungs-Kohärenz-Score zwischen Sprachversionen',
      'Divergenz-Warnungen bei auseinanderlaufenden Sprachversionen',
      'Priorisierter Handlungsbedarf',
      'Direkte Korrektur aus dem Dashboard',
    ],
    benefit: 'Sie sehen sofort, welche Seiten unvollständig sind, wo Links nicht mehr funktionieren und wo Sprachversionen inhaltlich auseinanderlaufen. Qualitätskontrolle wird zum kontinuierlichen Prozess statt zum manuellen Audit.',
    example: 'Eine deutsche Produktseite wird aktualisiert, die französische Version bleibt unverändert. Das Dashboard markiert die Divergenz automatisch und legt der zuständigen Redakteurin eine Aufgabe an.',
  },
  'stack-ai-ready': {
    sum: 'RAG, MCP, llms.txt',
    long: 'Machen Sie Website-Inhalte für Besucher und KI-Agenten gleichermassen zugänglich. Der RAG-basierte Website-Assistent antwortet ausschliesslich aus Ihren publizierten Inhalten. MCP-Server und llms.txt liefern dieselben Inhalte strukturiert für externe KI-Agenten.',
    items: [
      'Website-Assistent (RAG) für Besucher und Redakteure',
      'Antworten ausschliesslich aus publizierten Website-Inhalten',
      'MCP-Endpunkt pro Website',
      'Automatisch generierte llms.txt',
      'Standardisierter Zugriff für externe KI-Agenten',
      'Versionierte Prompts und reproduzierbare Antworten',
      'Rollen- und Mandantentrennung',
    ],
    benefit: 'Sie bieten Besuchern einen FAQ-Assistenten ohne Halluzinationsrisiko und positionieren Ihre Website als KI-zugängliches Asset. Stack CMS bringt AI-Discoverability als natives Feature mit — ohne nachträglich angebauten Layer.',
    example: 'Ein Besucher fragt im Chat nach einem Produktdetail. Der Assistent antwortet ausschliesslich aus den publizierten Inhalten. Parallel ruft ein externer KI-Agent dieselbe Information über den MCP-Endpunkt strukturiert ab.',
  },

  // ---------- cloudtec Module ----------
  shop: {
    sum: 'Katalog, Checkout, Bestellungen',
    long: 'Integrieren Sie digitale und physische Produkte in bestehende Plattformen und Kundenportale. Preis-, Rabatt- und Bestelllogik decken B2B- und B2C-Szenarien gleichermassen ab.',
    items: ['Produktkataloge', 'Warenkorb und Checkout', 'Preis- und Rabattlogiken', 'Bestellungen und Rechnungen', 'Lager- und Verfügbarkeiten', 'B2B- und B2C-Funktionen', 'Zahlungsintegration', 'Kundenkonten'],
    benefit: 'Sie öffnen bestehende Geschäftsprozesse für Self-Service-Bestellungen. Kundenkonten, Verfügbarkeiten und Zahlungsintegration arbeiten direkt mit Ihrer Plattform.',
    example: 'Geschäftskunden bestellen Produkte und Dienstleistungen über ein integriertes Kundenportal. Preise und Rabatte werden anhand der Kundenstammdaten automatisch angewendet.',
  },
  calendar: {
    sum: 'Termine, Buchungen, Ressourcen',
    long: 'Verwalten Sie Termine, Ressourcen und Verfügbarkeiten innerhalb einer Plattform. Erinnerungen, Serientermine und Kalendersynchronisation sind integriert und mit bestehenden Prozessen verknüpfbar.',
    items: ['Terminplanung und Buchungen', 'Ressourcen- und Verfügbarkeitsverwaltung', 'Serientermine', 'Erinnerungen und Benachrichtigungen', 'Kalendersynchronisation (CalDAV, iCal)', 'Reservierungen und Freigaben', 'Kalenderfreigaben pro Rolle', 'Buchungsworkflows'],
    benefit: 'Sie betreiben Buchungssysteme, Event-Plattformen und interne Ressourcenplanung auf einer Basis. Termine synchronisieren sich automatisch mit bestehenden Kalendern der Benutzer.',
    example: 'Ein Coworking-Anbieter verwaltet Räume, Geräte und Teamzeiten in einem System. Buchungen werden bestätigt oder umgebucht — der Verlauf bleibt nachvollziehbar.',
  },
  support: {
    sum: 'Tickets, SLA, Kundenportal',
    long: 'Bearbeiten Sie Supportanfragen strukturiert über Tickets, Eskalationen und SLAs. Kommunikationsverläufe, FAQ-Bereiche und interne Notizen liegen pro Vorgang gebündelt.',
    items: ['Ticketing mit Status und Prioritäten', 'SLA-Verwaltung und Eskalationen', 'FAQ- und Wissensbereiche', 'Kommunikationsverläufe pro Vorgang', 'Interne Notizen und externe Antworten', 'Rollenbasierte Bearbeitung', 'Integration in Kundenportale', 'Reporting zu Reaktionszeiten'],
    benefit: 'Sie bündeln interne Service-Prozesse und externe Kundenkommunikation in einer Oberfläche. SLAs und Eskalationen halten Reaktionszeiten messbar und nachvollziehbar.',
    example: 'Ein Kunde meldet eine Störung über das Kundenportal. Der Prozess folgt definierten SLAs, eskaliert bei Überschreitung automatisch und dokumentiert jede Antwort revisionssicher.',
  },
  workflow: {
    sum: 'Genehmigungen, Status, Automatisierung',
    long: 'Bilden Sie mehrstufige Geschäftsprozesse digital ab. Genehmigungen, Statusübergänge, Aufgaben und Automatisierungen lassen sich mit Rollen, Benachrichtigungen und APIs verknüpfen.',
    items: ['Mehrstufige Genehmigungsprozesse', 'Konfigurierbare Statusübergänge', 'Aufgabenverteilung und Eskalationen', 'Bedingte Logik und Verzweigungen', 'Integration in Rollen und Berechtigungen', 'Benachrichtigungen bei Prozessschritten', 'Anbindung an APIs und KI-Agenten', 'Auditierbarer Prozessverlauf'],
    benefit: 'Sie digitalisieren bestehende Genehmigungs- und Freigabeprozesse ohne Code. Jeder Prozessschritt bleibt mit Benutzer, Zeitstempel und Entscheidung dokumentiert.',
    example: 'Ein Spesenantrag durchläuft Vorgesetzte, Buchhaltung und Geschäftsleitung. Bei Fristüberschreitung eskaliert der Prozess automatisch an die nächste zuständige Instanz.',
  },
  newsletter: {
    sum: 'Listen, Templates, Tracking',
    long: 'Versenden Sie E-Mail-Kampagnen mit segmentierten Verteilerlisten, Templates und Versand-Automationen. Tracking liefert Klick-, Öffnungs- und Bounce-Raten direkt in der Plattform.',
    items: ['Verteilerlisten und Segmentierung', 'Dynamische Listen basierend auf Plattformdaten', 'Template-Verwaltung', 'Personalisierte Inhalte', 'Versand-Automationen', 'Trigger-basierte Kampagnen', 'Tracking (Öffnungen, Klicks, Bounces)', 'Integration in CRM und CMS'],
    benefit: 'Sie steuern Kampagnen zielgruppengenau aus einer Plattform. Verteilerlisten aktualisieren sich automatisch anhand der Daten aus Ihren Plattform-Prozessen.',
    example: 'Ein E-Commerce-Shop versendet wöchentliche Newsletter mit personalisierten Produktempfehlungen. Die Empfängerliste aktualisiert sich automatisch anhand des Kaufverhaltens der Kunden.',
  },
  tenant: {
    sum: 'Marken, Designs, Datentrennung',
    long: 'Betreiben Sie mehrere Mandanten, Marken oder Organisationen auf derselben Plattformbasis. Daten, Benutzer, Konfigurationen und Designs bleiben logisch getrennt — die Administration läuft zentral.',
    items: ['Mandantenspezifische Datentrennung', 'Eigene Benutzer und Rollen pro Mandant', 'Individuelle Konfigurationen', 'Mandantenspezifische Designs und Layouts', 'Übergreifende zentrale Administration', 'Mandantenspezifische Workflows', 'Reporting pro Mandant', 'Skalierung auf beliebig viele Mandanten'],
    benefit: 'Sie skalieren von einer ersten Marke auf hunderte Mandanten ohne separate Instanzen. Konfiguration, Branding und Workflows bleiben pro Mandant individuell steuerbar.',
    example: 'Eine Holding betreibt fünf Tochtermarken auf einer gemeinsamen Plattform. Jede Tochter hat eigenes Branding, eigene Workflows und eigene Benutzer — die Administration läuft zentral.',
  },
  sign: {
    sum: 'Anbindung, Workflow, Audit',
    long: 'Binden Sie externe Signatur-Services wie Skribble oder DocuSign in Ihre Plattform ein. Signaturprozesse werden aus Workflows ausgelöst — Rechtsgültigkeit und Identitätsprüfung liefert der jeweilige Anbieter.',
    items: ['Anbindung an Skribble, DocuSign und weitere Signatur-Services', 'Auslösen von Signaturprozessen aus Workflows', 'Statussynchronisation mit dem Service', 'Versionierte Ablage der signierten Dokumente', 'Audit-Trail über den Signaturprozess', 'Mobile Signatur über den Anbieter', 'Unterstützung für FES, AES und QES (je nach Service)', 'Konformität mit ZertES und eIDAS (je nach Service)'],
    benefit: 'Sie integrieren etablierte Signatur-Services in bestehende Prozesse. Rechtsgültigkeit und Identitätsprüfung liegen beim Anbieter — die Plattform liefert Prozesskontext und revisionssichere Ablage.',
    example: 'Ein neuer Mitarbeitender erhält den Arbeitsvertrag über Skribble zur Signatur. Der Plattform-Workflow löst den Signaturprozess aus und legt das signierte Dokument versioniert im Mitarbeiter-Profil ab.',
  },
  payment: {
    sum: 'Stripe, Twint, Postfinance',
    long: 'Integrieren Sie Online-Zahlungen über etablierte Anbieter direkt in Ihre Plattform. Abonnemente, Rechnungszahlungen, Teilzahlungen und Refunds werden zentral verwaltet und protokolliert.',
    items: ['Anbindung an Stripe, Datatrans, Payrexx, Postfinance, Twint und weitere', 'Einmalzahlungen', 'Abonnemente und wiederkehrende Zahlungen', 'Teilzahlungen und Ratenmodelle', 'Refunds und Stornierungen', 'Zahlungsstatus-Protokollierung', 'Rechnungserstellung', 'Webhook-basierte Synchronisation'],
    benefit: 'Sie wickeln Zahlungen direkt in Geschäftsprozessen ab — von Einmalkäufen bis zu wiederkehrenden Abonnementen. Jede Transaktion bleibt auditierbar nachverfolgbar.',
    example: 'Ein Mitglied bucht einen Jahresbeitrag und zahlt per Twint. Die Plattform aktiviert das Abo, dokumentiert die Zahlung und stellt eine Rechnung als PDF aus.',
  },
  solr: {
    sum: 'Volltext, Facetten, Synonyme',
    long: 'Durchsuchen Sie grosse Daten- und Dokumentenmengen performant mit Apache Solr. Volltextsuche, Facetten, Relevanzgewichtung und Synonyme sind flexibel konfigurierbar.',
    items: ['Volltextsuche auf grossen Datenmengen', 'Facettierung und Filter', 'Relevanzgewichtung und Boosts', 'Synonyme und Stoppwörter', 'Autocomplete und Suchvorschläge', 'Mehrdomänen-Indizes', 'Konfigurierbare Indizierung', 'Performance-Optimierung über Sharding'],
    benefit: 'Sie liefern Suchresultate auch bei Millionen Datensätzen in Millisekunden. Synonyme und Boosts steuern, was Benutzer zuerst sehen.',
    example: 'Ein Produktkatalog mit 200\'000 Artikeln wird in Solr indexiert. Benutzer finden passende Produkte über Volltext, Facetten und semantische Vorschläge in Echtzeit.',
  },
  maps: {
    sum: 'Karten, Standorte, Umkreis',
    long: 'Binden Sie geografische Informationen direkt in Ihre Plattform ein. Kartenansichten, Geokoordinaten, Standortsuche und Routing decken Plattformen mit räumlichem Bezug ab.',
    items: ['Kartenanbieter (swisstopo, camp2camp, Google Maps)', 'Geokoordinaten und Geocoding', 'Standort- und Umkreissuche', 'Routing und Distanzberechnung', 'Regionale Datenanalysen', 'Markierungen und Cluster', 'Polygone und Gebietsdefinitionen', 'Heatmaps'],
    benefit: 'Sie zeigen Standorte, Verfügbarkeiten und Routen direkt im Kontext der Anwendung. Umkreissuche und Routing greifen in bestehende Workflows.',
    example: 'Ein Servicebetrieb zeigt Kunden alle Standorte im 10-Kilometer-Umkreis. Die Route zum nächsten verfügbaren Standort wird direkt berechnet und angezeigt.',
  },
  sso: {
    sum: 'SAML2, OIDC, OAuth2',
    long: 'Authentifizieren Sie Benutzer zentral über bestehende Identity Provider. SAML2, OpenID Connect und OAuth2 lassen sich gegen Microsoft Entra ID, Keycloak oder weitere Anbieter konfigurieren.',
    items: ['SAML2 Single Sign-On', 'OpenID Connect', 'OAuth2', 'Just-in-Time-Provisionierung', 'Rollen-Mapping aus dem Identity Provider', 'Multi-IdP-Setup', 'Logout-Synchronisation', 'Audit-Logging der Anmeldungen'],
    benefit: 'Sie melden Mitarbeitende mit dem bestehenden Unternehmensaccount an. Berechtigungen werden automatisch aus dem Identity Provider gemappt.',
    example: 'Mitarbeitende eines Unternehmens melden sich über Microsoft Entra ID an. Neue Mitarbeitende erhalten ihre Rollen automatisch beim ersten Login per Just-in-Time-Provisionierung.',
  },
  audit: {
    sum: 'Revision, Compliance, Nachweis',
    long: 'Protokollieren Sie fachliche und technische Änderungen revisionssicher. Daten, Berechtigungen, Workflows und Systemkonfigurationen bleiben über die gesamte Aufbewahrungsfrist nachvollziehbar.',
    items: ['Revisionssichere Protokollierung', 'Änderungen an Datensätzen', 'Änderungen an Berechtigungen', 'Workflow-Audit-Trails', 'Systemkonfigurationsänderungen', 'Exportierbare Audit-Berichte', 'Aufbewahrungsregeln', 'Unterstützung für DSGVO, ISO 27001 und branchenspezifische Standards'],
    benefit: 'Sie weisen jede Änderung auch nach Jahren lückenlos nach. Compliance-Reviews und externe Audits sind dadurch deutlich entlastet.',
    example: 'Ein externer Auditor prüft, wer in den letzten zwölf Monaten Patientendaten eingesehen hat. Die Plattform liefert den vollständigen Bericht als revisionssicheren Export.',
  },
  forms: {
    sum: 'Felder, Bedingungen, Workflows',
    long: 'Erstellen Sie dynamische Formulare ohne Programmierung. Felder, Validierungen, bedingte Logik und Datenzuweisungen lassen sich direkt mit Workflows, APIs und Benachrichtigungen verknüpfen.',
    items: ['Drag-and-Drop-Formulareditor', 'Verschiedene Feldtypen', 'Validierungsregeln', 'Bedingte Felder und Sektionen', 'Datenzuweisung an Datenmodelle', 'Anbindung an Workflows', 'Mehrsprachige Formulare', 'Datenexport und API-Integration'],
    benefit: 'Sie publizieren neue Erfassungsformulare ohne Entwicklungsaufwand. Eingaben fliessen direkt in Workflows oder externe Systeme.',
    example: 'Eine HR-Abteilung erstellt ein Onboarding-Formular mit bedingten Feldern. Bei der Eingabe «externer Mitarbeiter» erscheinen zusätzliche Felder zur Sicherheitsfreigabe.',
  },
  apiplat: {
    sum: 'Entwicklerportal, Keys, Monitoring',
    long: 'Stellen Sie öffentliche oder partnerbasierte APIs kontrolliert bereit. Entwicklerportal, Dokumentation, API-Keys, Monitoring und Zugriffskontrollen sind vollständig integriert.',
    items: ['Entwicklerportal mit Self-Service', 'API-Dokumentation (OpenAPI)', 'API-Key-Verwaltung', 'Tiered-Access-Modelle', 'Monitoring und Analytics', 'Versionierung', 'Rate-Limiting pro Konsument', 'Zugriffskontrollen und Quotas'],
    benefit: 'Sie öffnen Ihre Plattform sicher für Partner und Drittentwickler. Nutzung, Versionen und Zugriffe bleiben zentral steuerbar.',
    example: 'Ein Partner registriert sich im Entwicklerportal, erhält API-Keys mit definierten Limits und ruft die Dokumentation online ab. Nutzung und Fehler werden im Plattform-Monitoring sichtbar.',
  },
};

// EN-Slot: noch nicht übersetzt → Fallback auf DE im Lookup.
const MODULE_DETAILS_EN = {
  // ---------- Basis ----------
  user: {
    sum: 'Users, roles, self-service',
    long: 'You manage users, roles and organisations centrally on one platform. Self-service functions for registration, password reset and profile maintenance reduce the load on administration and support.',
    items: [
      'User and role management',
      'Organisations and groups',
      'Self-service registration',
      'Password reset and user activation',
      'User profiles and settings',
      'Delegations and deputies',
      'Role-based platform access',
      'Integration with SSO and MFA',
    ],
    benefit: 'New employees set up their access independently and receive the appropriate roles automatically. Every access remains traceable in an auditable user overview.',
    example: 'Employees of a company register through the self-service function. The platform assigns roles automatically based on the email domain and the organisational membership.',
  },
  perm: {
    sum: 'Roles, permissions, inheritance',
    long: 'You control access at fine granularity through roles, objects and status. Permission inheritance, tenant context and approval logic cover even complex organisational structures.',
    items: [
      'Role-based permissions',
      'Object- and record-based access',
      'Dynamic permissions depending on status or context',
      'Visibility control',
      'Release and approval rights',
      'Tenant-dependent permissions',
      'Permission inheritance',
      'Auditable permission changes',
    ],
    benefit: 'You map external partners, internal teams and multiple tenants in a single permission model. Every change to permissions is logged with user, timestamp and previous state.',
    example: 'An external partner sees only their own projects. Internal employees additionally receive the administration functions that match their role.',
  },
  auth: {
    sum: 'Login, MFA, passkeys',
    long: 'You protect access with modern authentication methods. MFA, passkeys, session management and password policies are auditable throughout.',
    items: [
      'Username/password login',
      'Multi-factor authentication (MFA)',
      'Passkey support',
      'Session management',
      'Password policies',
      'Device and access validation',
      'Brute-force protection',
      'Authentication logging',
    ],
    benefit: 'You protect internal and external access with combinable methods. Authentication events flow into central logging and are available for security reviews.',
    example: 'Employees sign in with a passkey. External partners use MFA via an authenticator app — every login attempt is logged.',
  },
  data: {
    sum: 'Structured, validated, versioned',
    long: 'You model business data with clear relationships, status and lifecycle rules. Validations run server-side and keep data quality stable even with many parallel editors.',
    items: [
      'Structured data models',
      'Validations and data rules',
      'Historisation and versioning',
      'Relationships between records',
      'Status and lifecycle management',
      'Data integrations',
      'Workflow dependencies',
      'Import and synchronisation processes',
    ],
    benefit: 'You keep master data, transactional data and audit information cleanly separated. Versioning makes changes traceable across the entire data history.',
    example: 'Patient records, referrals and status changes are stored with versioning. Every previous state remains reconstructable with user and timestamp.',
  },
  lists: {
    sum: 'Sort, filter, export',
    long: 'You process large data volumes in dynamic tables with filters, grouping and saved views. Bulk actions and inline editing accelerate daily work.',
    items: [
      'Dynamic table views',
      'Filter and search functions',
      'Sorting and grouping',
      'Saved views',
      'Bulk actions',
      'Role-dependent views',
      'Inline editing',
      'Export functions',
    ],
    benefit: 'You analyse thousands of records without performance loss. Saved views and role-based filters adapt to every use case.',
    example: 'A case worker filters all open cases with high priority. The filtered view is saved and set up as a recurring Excel export.',
  },
  api: {
    sum: 'REST, versioned, documented',
    long: 'You provide structured interfaces for internal applications and third-party systems. REST and GraphQL endpoints are versioned, token-authenticated and rate-limited.',
    items: [
      'REST and GraphQL APIs',
      'API versioning',
      'Authentication and tokens',
      'Rate limiting',
      'API documentation',
      'Monitoring and logging',
      'Webhooks and events',
      'Role-based API access',
    ],
    benefit: 'You connect ERP, CRM and line-of-business systems through documented endpoints. Webhooks and API logs make every data movement traceable.',
    example: 'An ERP system synchronises customer data and invoice information with the platform. Status changes are reported back to the ERP through signed webhooks.',
  },
  search: {
    sum: 'Full text, semantic, faceted',
    long: 'You search content, data and documents across all domains. Synonyms, weightings and autocomplete deliver relevant results even for fuzzy terms.',
    items: [
      'Full-text search',
      'Facets and filters',
      'Synonyms and weightings',
      'Multi-domain search',
      'Document search',
      'Semantic search functions',
      'Role-dependent search results',
      'Search suggestions and autocomplete',
    ],
    benefit: 'You find projects, documents and tickets in a single search query. Role-dependent results automatically respect existing permissions.',
    example: 'A user searches for a customer number. The result list shows open tickets, ongoing projects and related documents in a single view.',
  },
  cache: {
    sum: 'Application, API, edge',
    long: 'You accelerate platform responses through multi-layer caching. Application, API and edge cache work in a coordinated way and relieve the database in a targeted manner.',
    items: [
      'Server and application caching',
      'Database caching',
      'API caching',
      'CDN integration',
      'Cache invalidation',
      'Session caching',
      'Edge caching',
      'Scaling optimisations',
    ],
    benefit: 'You keep response times stable even under high load and with large data volumes. Event-driven cache invalidation keeps data current at the same time.',
    example: 'Frequently called lists are cached and delivered without database access. Changes invalidate the cache automatically.',
  },
  'admin-ui': {
    sum: 'Configuration in the browser',
    long: 'You administer users, roles, workflows and system settings through a central interface. Dashboards and audit overviews are directly integrated.',
    items: [
      'User and role management',
      'Configuration interfaces',
      'Dashboards and reports',
      'Workflow control',
      'Data management',
      'Permission management',
      'System configurations',
      'Audit and activity overviews',
    ],
    benefit: 'You configure platform modules without code changes. Business owners steer processes themselves — development stays free for more complex adjustments.',
    example: 'Administrators create a new workflow including approval steps directly in the browser. Changes take effect immediately, without a deployment.',
  },
  media: {
    sum: 'Upload, variants, CDN',
    long: 'You manage images, videos and documents centrally in a media library. Variant generation, image optimisation and CDN delivery happen automatically.',
    items: [
      'File uploads',
      'Media library',
      'Variant generation',
      'Image optimisation',
      'Metadata management',
      'Access control',
      'CDN delivery',
      'Versioning',
    ],
    benefit: 'You deliver media with high performance over the CDN. Access control and versioning apply also to sensitive or customer-specific content.',
    example: 'An uploaded product image is automatically generated in several sizes and delivered optimised. Access to the original image is restricted to internal roles.',
  },
  export: {
    sum: 'CSV, Excel, JSON',
    long: 'You export data ad hoc or on a schedule into common formats. Filters, role permissions and export history are integrated throughout.',
    items: [
      'CSV and Excel export',
      'JSON and XML export',
      'Scheduled exports',
      'Role-based export permissions',
      'Filter-dependent data exports',
      'PDF generation',
      'API-based exports',
      'Export history',
    ],
    benefit: 'You hand over analyses to Excel, BI tools or external systems. Scheduled exports run reliably in the background without manual effort.',
    example: 'A monthly project report is generated automatically as an Excel file and sent to controlling. The export history documents every run.',
  },
  notify: {
    sum: 'Email, push, in-app',
    long: 'You manage notifications centrally across email, push, SMS and in-app channels. Templates, multilingualism and escalations are integrated.',
    items: [
      'Email notifications',
      'Push messages',
      'In-app notifications',
      'SMS delivery',
      'Template management',
      'Multilingual notifications',
      'Escalations',
      'Delivery status and logs',
    ],
    benefit: 'You inform users promptly about status changes and approval steps. Delivery status and logs make every notification traceable.',
    example: 'When a case status changes, responsible employees receive an in-app notification. If no reaction follows, the system escalates by email after a defined deadline.',
  },

  // ---------- Helpers ----------
  i18n: {
    sum: 'Maintain content in multiple languages',
    long: 'You maintain content, interfaces and system texts in multiple languages. Translation workflows, fallback languages and language-specific notifications are integrated.',
    items: [
      'Multilingual content',
      'Language-dependent user interfaces',
      'Translation workflows',
      'Fallback languages',
      'Multilingual emails',
      'Language-specific content',
      'Language switching',
      'Translation management',
    ],
    benefit: 'You operate international platforms with consistent language handling. Users switch the language at any time without data loss or context break.',
    example: 'A platform is operated in German, French and Italian in parallel. Notifications are sent automatically in the language of the respective recipient.',
  },
  reporting: {
    sum: 'KPIs, trends, drill-downs',
    long: 'You analyse platform data in real time through configurable dashboards. Charts, drill-downs and time-based reports cover operational and strategic questions.',
    items: [
      'KPI dashboards',
      'Charts and statistics',
      'Real-time analytics',
      'Export functions',
      'Filters and drill-downs',
      'Time-based reports',
      'Role-dependent dashboards',
      'Custom report definitions',
    ],
    benefit: 'You see project status, ticket volume and revenue development in a single overview. Role-dependent dashboards deliver the relevant metrics to each function.',
    example: 'The executive board reviews revenue and order development weekly through a central dashboard. Drill-downs lead directly to individual cases.',
  },
  pdf: {
    sum: 'Invoices, contracts, reports',
    long: 'You generate invoices, contracts and reports server-side from platform data. Templates, multilingualism and digital signatures are integrated.',
    items: [
      'PDF generation',
      'Template management',
      'Multilingual documents',
      'Invoices and contracts',
      'Automated dispatch',
      'Digital signatures',
      'Versioning',
      'Layout customisation',
    ],
    benefit: 'You create documents automatically and consistently from ongoing business processes. Versioning and layout adjustments remain controllable at all times.',
    example: 'An order is completed, the system generates the invoice as a PDF and dispatches it automatically. A versioned copy stays in the customer file.',
  },
  logging: {
    sum: 'Structured audit trails',
    long: 'You log technical and business activities in an audit-compliant way. User actions, API calls and system events remain traceable throughout.',
    items: [
      'Audit trails',
      'User activities',
      'API logging',
      'Error logging',
      'Status changes',
      'System events',
      'Security logs',
      'Exportable logs',
    ],
    benefit: 'You provide gap-free evidence of changes to sensitive records. Exportable logs support compliance reviews and internal audits.',
    example: 'Every change to a patient record is logged with user, timestamp and previous state. The audit log can be exported for an audit review.',
  },

  // ---------- AI ----------
  'ai-gateway': {
    sum: 'Central layer for external and local models',
    long: 'A central AI integration layer to connect and manage different large language models (LLMs). External AI services and locally operated models are addressed in a uniform way.',
    items: [
      'Unified API for GPT, Claude, Mistral, local LLMs',
      'Model routing by cost, performance, data classification',
      'Access control and multi-tenancy',
      'Cost and usage monitoring per team/tenant',
      'API management, rate limits, caching',
      'Security and compliance layer (PII masking optional)',
    ],
    benefit: 'You integrate AI functions into your platform and business processes in a controllable, traceable way and independent of individual vendors — without lock-in.',
    example: 'The marketing team uses OpenAI, legal uses a local model — both through the same platform API. Models can be switched without code changes in the applications.',
  },
  'ai-pipelines': {
    sum: 'Pipelines, RAG, document AI on-prem',
    long: 'Custom AI processing pipelines for specific business processes and data flows. Locally hosted models within your own infrastructure — sensitive data never leaves the controlled environment.',
    items: [
      'Local hosting of open-source LLMs',
      'Document analysis, classification, extraction',
      'Semantic search and Retrieval-Augmented Generation (RAG)',
      'Connection to documents, databases, APIs, existing platforms',
      'Vector databases and embedding pipelines',
      'Versioned prompts and reproducible workflows',
    ],
    benefit: 'Custom, data-protection-compliant AI workflows with high flexibility and traceability. Company knowledge is made available to AI in a structured way.',
    example: 'Contract analysis: uploaded PDFs are classified locally, clauses extracted, risks marked — no contract leaves the Swiss hosting.',
  },
  'ai-agents': {
    sum: 'Agents orchestrate ERP/CRM/line-of-business systems',
    long: 'Intelligent AI agents to automate complex business processes. Agents analyse tasks, prepare decisions, connect to external systems and orchestrate multi-step workflows.',
    items: [
      'Standardised tool integration (MCP, OpenAPI)',
      'Connection to ERP, CRM, line-of-business systems',
      'Workflow engine: classic processes plus AI decisions',
      'Approvals, escalations, role permissions',
      'Human-in-the-loop at critical steps',
      'Full traceability of every action',
    ],
    benefit: 'Complex business processes become controllable, traceable AI workflows — with clear handover points to humans.',
    example: 'Supplier onboarding: the agent checks documents, queries SAP/CRM, proposes a risk classification — the compliance officer confirms.',
  },
  'ai-ux': {
    sum: 'Natural language, auto-fill, semantic search',
    long: 'Classic user interfaces are extended with intelligent AI functions. Users search, analyse and trigger processes through natural language.',
    items: [
      'AI search and semantic search in the UI',
      'Intelligent form support and auto-fill',
      'Automatic data extraction from documents',
      'Context-aware suggestions and explanations',
      'Multi-modal: text, image, voice',
      'Learning capability through user feedback',
    ],
    benefit: 'Modern, user-friendly platforms with reduced training effort. Users reach the desired result faster.',
    example: 'A user types "All open invoices from Q3 from customers in Ticino" — the system filters, groups and proposes an export format.',
  },

  // ---------- Stack CMS by cloudtec ----------
  'stack-cms': {
    sum: 'Page builder, editor, headless API',
    long: 'You maintain websites through a block-based page builder with live preview and headless API delivery. Multilingual workflows per tenant and granular role control are part of the foundation.',
    items: [
      'Page builder with block system',
      'Rich text editor (WYSIWYG)',
      'Multilingualism and translation workflows',
      'Media management with variant generation',
      'Live preview for desktop, tablet and mobile',
      'Roles and permissions per tenant',
      'Headless API (REST)',
      'Delivery to Next.js, Nuxt, native apps or static sites',
    ],
    benefit: 'You maintain any number of customer websites with multiple language versions on a single instance. What appears in the preview matches exactly what is published on the frontend.',
    example: 'An agency runs 30 customer websites on one instance. Each customer maintains content in German, French and English — with their own roles, their own layout and delivery to different frontend technologies.',
  },
  'stack-ai-writing': {
    sum: 'Write, translate, optimise',
    long: 'You accelerate text creation and translation directly in the editor. Writing Assistant, text-to-page and AI translation work with your tenant’s tone profile and auto-glossary.',
    items: [
      'Writing Assistant (generate, improve, translate in the editor)',
      'Text-to-page (complete pages from prompt or template)',
      'Tone profile and auto-glossary per tenant',
      'AI pre-translations at the push of a button',
      'SEO assistant (meta title, description, heading hierarchy, keywords)',
      'Internal link suggestions on a semantic basis',
      'AI tagging and alt-text generation for media',
    ],
    benefit: 'You create multilingual content in minutes instead of hours — with consistent tone, optimised SEO and accessible alt texts. Writing style and vocabulary of each tenant are applied consistently.',
    example: 'An editor writes an article in German. The AI translates it into two further languages, suggests internal links and adds meta data — all in the same editing flow.',
  },
  'stack-dashboard': {
    sum: 'Completeness, links, coherence',
    long: 'You keep an overview per tenant of quality and consistency across all content. Completeness score, broken-link detection, SEO status and translation coherence are available in an aggregated view.',
    items: [
      'Completeness score per page',
      'Automatic broken-link detection (internal and external)',
      'Aggregated SEO status across all pages',
      'Translation coherence score between language versions',
      'Divergence warnings when language versions drift apart',
      'Prioritised action items',
      'Direct correction from the dashboard',
    ],
    benefit: 'You see immediately which pages are incomplete, where links no longer work and where language versions diverge in content. Quality control becomes a continuous process instead of a manual audit.',
    example: 'A German product page is updated, the French version stays unchanged. The dashboard marks the divergence automatically and creates a task for the responsible editor.',
  },
  'stack-ai-ready': {
    sum: 'RAG, MCP, llms.txt',
    long: 'You make website content equally accessible to visitors and AI agents. The RAG-based website assistant answers exclusively from your published content. MCP server and llms.txt deliver the same content in a structured form for external AI agents.',
    items: [
      'Website assistant (RAG) for visitors and editors',
      'Answers exclusively from published website content',
      'MCP endpoint per website',
      'Automatically generated llms.txt',
      'Standardised access for external AI agents',
      'Versioned prompts and reproducible answers',
      'Role and tenant separation',
    ],
    benefit: 'You offer visitors a FAQ assistant without hallucination risk and position your website as an AI-accessible asset. Stack CMS brings AI discoverability as a native feature — not as a layer bolted on afterwards.',
    example: 'A visitor asks in the chat about a product detail. The assistant answers exclusively from the published content. In parallel, an external AI agent retrieves the same information in a structured form through the MCP endpoint.',
  },

  // ---------- cloudtec Modules ----------
  shop: {
    sum: 'Catalogue, checkout, orders',
    long: 'You integrate digital and physical products into existing platforms and customer portals. Pricing, discount and order logic cover B2B and B2C scenarios equally.',
    items: ['Product catalogues', 'Shopping cart and checkout', 'Pricing and discount logic', 'Orders and invoices', 'Stock and availability', 'B2B and B2C functions', 'Payment integration', 'Customer accounts'],
    benefit: 'You open existing business processes for self-service orders. Customer accounts, availabilities and payment integration work directly with your platform.',
    example: 'Business customers order products and services through an integrated customer portal. Prices and discounts are applied automatically based on the customer master data.',
  },
  calendar: {
    sum: 'Appointments, bookings, resources',
    long: 'You manage appointments, resources and availabilities within a single platform. Reminders, recurring appointments and calendar synchronisation are integrated and can be linked to existing processes.',
    items: ['Appointment scheduling and bookings', 'Resource and availability management', 'Recurring appointments', 'Reminders and notifications', 'Calendar synchronisation (CalDAV, iCal)', 'Reservations and approvals', 'Calendar sharing per role', 'Booking workflows'],
    benefit: 'You operate booking systems, event platforms and internal resource planning on one basis. Appointments synchronise automatically with users’ existing calendars.',
    example: 'A coworking provider manages rooms, equipment and team hours in one system. Bookings are confirmed or rescheduled — the history remains traceable.',
  },
  support: {
    sum: 'Tickets, SLA, customer portal',
    long: 'You handle support requests in a structured way through tickets, escalations and SLAs. Communication histories, FAQ areas and internal notes are bundled per case.',
    items: ['Ticketing with status and priorities', 'SLA management and escalations', 'FAQ and knowledge areas', 'Communication history per case', 'Internal notes and external replies', 'Role-based handling', 'Integration with customer portals', 'Reporting on response times'],
    benefit: 'You bundle internal service processes and external customer communication in a single interface. SLAs and escalations keep response times measurable and traceable.',
    example: 'A customer reports an incident through the customer portal. The process follows defined SLAs, escalates automatically when exceeded and documents every reply in an audit-compliant way.',
  },
  workflow: {
    sum: 'Approvals, status, automation',
    long: 'You digitalise multi-step business processes. Approvals, status transitions, tasks and automations can be linked with roles, notifications and APIs.',
    items: ['Multi-step approval processes', 'Configurable status transitions', 'Task assignment and escalations', 'Conditional logic and branching', 'Integration with roles and permissions', 'Notifications at process steps', 'Connection to APIs and AI agents', 'Auditable process history'],
    benefit: 'You digitalise existing approval and release processes without code. Every process step remains documented with user, timestamp and decision.',
    example: 'An expense request passes through line manager, accounting and executive board. When deadlines are exceeded, the process escalates automatically to the next responsible party.',
  },
  newsletter: {
    sum: 'Lists, templates, tracking',
    long: 'You send email campaigns with segmented distribution lists, templates and dispatch automations. Tracking delivers click, open and bounce rates directly within the platform.',
    items: ['Distribution lists and segmentation', 'Dynamic lists based on platform data', 'Template management', 'Personalised content', 'Dispatch automations', 'Trigger-based campaigns', 'Tracking (opens, clicks, bounces)', 'Integration with CRM and CMS'],
    benefit: 'You manage campaigns with precise targeting from a single platform. Distribution lists update automatically based on data from your platform processes.',
    example: 'An e-commerce shop sends a weekly newsletter with personalised product recommendations. The recipient list updates automatically based on the customers’ purchasing behaviour.',
  },
  tenant: {
    sum: 'Brands, designs, data separation',
    long: 'You operate multiple tenants, brands or organisations on the same platform base. Data, users, configurations and designs remain logically separated — administration runs centrally.',
    items: ['Tenant-specific data separation', 'Own users and roles per tenant', 'Individual configurations', 'Tenant-specific designs and layouts', 'Cross-tenant central administration', 'Tenant-specific workflows', 'Reporting per tenant', 'Scaling to any number of tenants'],
    benefit: 'You scale from a first brand to hundreds of tenants without separate instances. Configuration, branding and workflows remain individually controllable per tenant.',
    example: 'A holding operates five subsidiary brands on a shared platform. Every subsidiary has its own branding, its own workflows and its own users — administration runs centrally.',
  },
  sign: {
    sum: 'Integration, workflow, audit',
    long: 'You integrate external signature services such as Skribble or DocuSign into your platform. Signature processes are triggered from workflows — legal validity and identity verification are provided by the respective vendor.',
    items: ['Integration with Skribble, DocuSign and further signature services', 'Triggering signature processes from workflows', 'Status synchronisation with the service', 'Versioned storage of signed documents', 'Audit trail across the signature process', 'Mobile signature via the vendor', 'Support for FES, AES and QES (depending on service)', 'Compliance with ZertES and eIDAS (depending on service)'],
    benefit: 'You integrate established signature services into existing processes. Legal validity and identity verification stay with the vendor — the platform delivers process context and audit-compliant storage.',
    example: 'A new employee receives the employment contract through Skribble for signature. The platform workflow triggers the signature process and stores the signed document with versioning in the employee profile.',
  },
  payment: {
    sum: 'Stripe, Twint, Postfinance',
    long: 'You integrate online payments through established providers directly into your platform. Subscriptions, invoice payments, partial payments and refunds are managed and logged centrally.',
    items: ['Integration with Stripe, Datatrans, Payrexx, Postfinance, Twint and others', 'One-off payments', 'Subscriptions and recurring payments', 'Partial payments and instalment models', 'Refunds and cancellations', 'Payment status logging', 'Invoice generation', 'Webhook-based synchronisation'],
    benefit: 'You process payments directly in business processes — from one-off purchases to recurring subscriptions. Every transaction remains auditable and traceable.',
    example: 'A member books an annual membership and pays by Twint. The platform activates the subscription, documents the payment and issues an invoice as a PDF.',
  },
  solr: {
    sum: 'Full text, facets, synonyms',
    long: 'You search large data and document volumes performantly with Apache Solr. Full-text search, facets, relevance weighting and synonyms can be configured flexibly.',
    items: ['Full-text search on large data volumes', 'Faceting and filters', 'Relevance weighting and boosts', 'Synonyms and stop words', 'Autocomplete and search suggestions', 'Multi-domain indexes', 'Configurable indexing', 'Performance optimisation through sharding'],
    benefit: 'You deliver search results in milliseconds even on millions of records. Synonyms and boosts control what users see first.',
    example: 'A product catalogue with 200,000 items is indexed in Solr. Users find matching products through full-text search, facets and semantic suggestions in real time.',
  },
  maps: {
    sum: 'Maps, locations, radius',
    long: 'You embed geographic information directly into your platform. Map views, geo coordinates, location search and routing cover platforms with a spatial dimension.',
    items: ['Map providers (swisstopo, camp2camp, Google Maps)', 'Geo coordinates and geocoding', 'Location and radius search', 'Routing and distance calculation', 'Regional data analysis', 'Markers and clusters', 'Polygons and area definitions', 'Heatmaps'],
    benefit: 'You show locations, availabilities and routes directly in the context of the application. Radius search and routing plug into existing workflows.',
    example: 'A service company shows customers all locations within a 10-kilometre radius. The route to the next available location is calculated and displayed directly.',
  },
  sso: {
    sum: 'SAML2, OIDC, OAuth2',
    long: 'You authenticate users centrally through existing identity providers. SAML2, OpenID Connect and OAuth2 can be configured against Microsoft Entra ID, Keycloak or other providers.',
    items: ['SAML2 single sign-on', 'OpenID Connect', 'OAuth2', 'Just-in-time provisioning', 'Role mapping from the identity provider', 'Multi-IdP setup', 'Logout synchronisation', 'Audit logging of sign-ins'],
    benefit: 'You sign employees in with their existing corporate account. Permissions are mapped automatically from the identity provider.',
    example: 'Employees of a company sign in through Microsoft Entra ID. New employees receive their roles automatically on first login through just-in-time provisioning.',
  },
  audit: {
    sum: 'Audit, compliance, evidence',
    long: 'You log business and technical changes in an audit-compliant way. Data, permissions, workflows and system configurations remain traceable across the entire retention period.',
    items: ['Audit-compliant logging', 'Changes to records', 'Changes to permissions', 'Workflow audit trails', 'System configuration changes', 'Exportable audit reports', 'Retention rules', 'Support for GDPR, ISO 27001 and industry-specific standards'],
    benefit: 'You provide gap-free evidence of every change even years later. Compliance reviews and external audits are significantly relieved as a result.',
    example: 'An external auditor checks who has accessed patient records over the past twelve months. The platform delivers the complete report as an audit-compliant export.',
  },
  forms: {
    sum: 'Fields, conditions, workflows',
    long: 'You create dynamic forms without programming. Fields, validations, conditional logic and data assignments can be linked directly to workflows, APIs and notifications.',
    items: ['Drag-and-drop form editor', 'Various field types', 'Validation rules', 'Conditional fields and sections', 'Data assignment to data models', 'Connection to workflows', 'Multilingual forms', 'Data export and API integration'],
    benefit: 'You publish new capture forms without development effort. Inputs flow directly into workflows or external systems.',
    example: 'An HR department creates an onboarding form with conditional fields. When "external employee" is selected, additional fields for security clearance appear.',
  },
  apiplat: {
    sum: 'Developer portal, keys, monitoring',
    long: 'You provide public or partner-based APIs in a controlled way. Developer portal, documentation, API keys, monitoring and access controls are fully integrated.',
    items: ['Developer portal with self-service', 'API documentation (OpenAPI)', 'API key management', 'Tiered access models', 'Monitoring and analytics', 'Versioning', 'Rate limiting per consumer', 'Access controls and quotas'],
    benefit: 'You open your platform securely to partners and third-party developers. Usage, versions and access stay centrally controllable.',
    example: 'A partner registers in the developer portal, receives API keys with defined limits and accesses the documentation online. Usage and errors become visible in the platform monitoring.',
  },
};

function getModuleDetail(id, locale) {
  if (locale === 'en' && MODULE_DETAILS_EN[id]) return MODULE_DETAILS_EN[id];
  return MODULE_DETAILS_DE[id] || null;
}

export { MODULE_DETAILS_DE, MODULE_DETAILS_EN, getModuleDetail };
